/**
 * El formulario, conectado a la API.
 *
 * Reúne lo que `form-state.ts` decide con lo que hace falta para que una
 * persona lo use: validar al salir del campo y no al teclear, avisar antes
 * de perder cambios, y contar el conflicto de versión como lo que es.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import {
  changedValues,
  mapServerErrors,
  toRequestBody,
  unplacedErrors,
  validateAll,
  validateField,
  type FieldErrors,
  type FieldRules,
  type FieldValue,
  type FormValues,
} from './form-state.js';

export interface UseFormOptions<Saved> {
  /**
   * Valores de partida.
   *
   * ⚠️ **Su CONTENIDO tiene que ser estable entre renders.** El objeto puede
   * ser nuevo cada vez —así una ficha que llega por red actualiza el
   * formulario—, pero si los valores cambian solos se entra en bucle: el
   * efecto de sincronización los reescribe, eso provoca otro render, y de
   * paso borra lo que se estuviera tecleando.
   *
   * Nada de `Math.random()`, `Date.now()` ni generadores aquí dentro. Si
   * hace falta un valor generado, guárdelo en un `useState` y páselo.
   */
  initial: FormValues;
  rules?: FieldRules;
  /** Campos que la API acepta como nulo para borrarlos. */
  nullable?: readonly string[];
  /** Guarda y devuelve lo que haga falta para continuar. */
  onSubmit: (body: FormValues, api: ReturnType<typeof useSession>['api']) => Promise<Saved>;
  onSaved?: (saved: Saved) => void;
  /** En edición, sólo se envía lo que cambió. */
  mode: 'create' | 'edit';
}

export interface UseFormResult {
  values: FormValues;
  errors: FieldErrors;
  /** Errores del servidor que no corresponden a ningún campo visible. */
  orphanErrors: string[];
  /** Mensaje general: fallo de red, permiso, conflicto… */
  formError: string | null;
  /** Alguien cambió el registro mientras se editaba. */
  versionConflict: boolean;
  dirty: boolean;
  saving: boolean;
  set: (field: string, value: FieldValue) => void;
  blur: (field: string) => void;
  submit: (event?: { preventDefault: () => void }) => Promise<void>;
  reset: (values?: FormValues) => void;
}

export function useForm<Saved>(options: UseFormOptions<Saved>): UseFormResult {
  const { api } = useSession();
  const rules = useMemo(() => options.rules ?? {}, [options.rules]);

  const [initial, setInitial] = useState<FormValues>(options.initial);
  const [values, setValues] = useState<FormValues>(options.initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [orphanErrors, setOrphanErrors] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [saving, setSaving] = useState(false);

  // La ficha puede cargarse después de montar el formulario.
  const initialKey = JSON.stringify(options.initial);
  useEffect(() => {
    const next = JSON.parse(initialKey) as FormValues;
    setInitial(next);
    setValues(next);
    setErrors({});
  }, [initialKey]);

  const dirty = Object.keys(changedValues(initial, values)).length > 0;

  // Avisa antes de cerrar la pestaña con cambios sin guardar. Sólo cubre
  // la salida del navegador; la navegación interna la guarda la pantalla.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current) event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const set = useCallback((field: string, value: FieldValue) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    // Al escribir se limpia el error de ese campo: mantenerlo mientras la
    // persona corrige es regañarla por algo que está arreglando.
    setErrors((previous) => {
      if (!(field in previous)) return previous;
      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  /** Validación en el borde del campo, al salir (ERP-UX.md §8). */
  const blur = useCallback(
    (field: string) => {
      setValues((current) => {
        const error = validateField(field, current, rules);
        setErrors((previous) => {
          if (error === null) {
            if (!(field in previous)) return previous;
            const next = { ...previous };
            delete next[field];
            return next;
          }
          return { ...previous, [field]: error };
        });
        return current;
      });
    },
    [rules],
  );

  const reset = useCallback(
    (next?: FormValues) => {
      const target = next ?? initial;
      setInitial(target);
      setValues(target);
      setErrors({});
      setOrphanErrors([]);
      setFormError(null);
      setVersionConflict(false);
    },
    [initial],
  );

  const submit = useCallback(
    async (event?: { preventDefault: () => void }) => {
      event?.preventDefault();
      if (saving) return;

      const found = validateAll(values, rules);
      if (Object.keys(found).length > 0) {
        setErrors(found);
        setFormError('Revise los campos marcados.');
        return;
      }

      setSaving(true);
      setFormError(null);
      setOrphanErrors([]);
      setVersionConflict(false);

      try {
        const payload =
          options.mode === 'edit' ? changedValues(initial, values) : values;

        if (options.mode === 'edit' && Object.keys(payload).length === 0) {
          setFormError('No hay cambios que guardar.');
          return;
        }

        const saved = await options.onSubmit(
          toRequestBody(payload, options.nullable ?? []),
          api,
        );

        setInitial(values);
        options.onSaved?.(saved);
      } catch (caught) {
        if (!(caught instanceof ApiError)) {
          setFormError('No se ha podido contactar con el servidor.');
          return;
        }

        if (caught.status === 409) {
          // No es «error 409»: es que alguien lo cambió mientras tanto, y
          // lo que hay que ofrecer es recargar, no reintentar a ciegas.
          setVersionConflict(true);
          setFormError(
            'Otra persona ha modificado este registro mientras lo editaba. Recargue para ver los cambios antes de guardar.',
          );
          return;
        }

        const mapped = mapServerErrors(caught.fieldErrors);
        setErrors(mapped);
        setOrphanErrors(unplacedErrors(mapped, Object.keys(values)));
        setFormError(
          Object.keys(mapped).length > 0 ? 'Revise los campos marcados.' : caught.message,
        );
      } finally {
        setSaving(false);
      }
    },
    [api, initial, options, rules, saving, values],
  );

  return {
    values,
    errors,
    orphanErrors,
    formError,
    versionConflict,
    dirty,
    saving,
    set,
    blur,
    submit,
    reset,
  };
}
